// Countdown banner shown above any generated image that lives in the cloud.
// Cloud images are auto-deleted 24 hours after creation, so we warn users to
// download before the timer hits zero.
//
// Two visual states:
//   - active  — orange/warning, with live "Xh Ym left" countdown + Download CTA
//   - expired — muted, with "Image expired — saved locally only" hint
//
// Compact mode (used in lists/cards) shows just a tiny pill instead of the
// full banner.
import { useEffect, useState } from "react";
import { AlertTriangle, Download, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface ExpiryBannerProps {
  /** ISO timestamp when the cloud image will be deleted. Null = no image saved. */
  expiresAt: string | null | undefined;
  /** Whether the image is still present in the cloud (i.e. URL not null). */
  hasImage: boolean;
  /** Called when the user taps Download. */
  onDownload?: () => void;
  /** Disable the download button (e.g. URL still resolving). */
  downloadDisabled?: boolean;
  /** Compact pill mode for use inside cards/tiles. */
  compact?: boolean;
  className?: string;
}

const formatRemaining = (ms: number): string => {
  if (ms <= 0) return "0m";
  const totalMins = Math.floor(ms / 60000);
  const h = Math.floor(totalMins / 60);
  const m = totalMins % 60;
  if (h <= 0) return `${m}m`;
  return `${h}h ${m}m`;
};

export const ExpiryBanner = ({
  expiresAt,
  hasImage,
  onDownload,
  downloadDisabled,
  compact,
  className,
}: ExpiryBannerProps) => {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!expiresAt) return;
    // Update every 30s — fine-grained enough for an hour/minute display.
    const i = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(i);
  }, [expiresAt]);

  // No image at all (older row or already swept) — show "expired" hint only
  // if the row used to have one (we don't actually know; render nothing if no
  // expiresAt was ever set).
  if (!expiresAt && !hasImage) return null;

  const expMs = expiresAt ? new Date(expiresAt).getTime() : 0;
  const remaining = expMs - now;
  const expired = !hasImage || remaining <= 0;

  if (compact) {
    return (
      <span
        className={cn(
          "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider",
          expired
            ? "bg-muted text-muted-foreground"
            : remaining < 60 * 60 * 1000
              ? "bg-destructive/15 text-destructive"
              : "bg-warning/15 text-warning",
          className,
        )}
      >
        <Clock className="h-3 w-3" />
        {expired ? "Expired" : `${formatRemaining(remaining)} left`}
      </span>
    );
  }

  if (expired) {
    return (
      <div
        className={cn(
          "flex items-start gap-2 rounded-xl border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground",
          className,
        )}
      >
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
        <span>
          Cloud image expired. The score and details are still here — but the picture is only on your phone if you downloaded it.
        </span>
      </div>
    );
  }

  const urgent = remaining < 60 * 60 * 1000; // < 1 hour

  return (
    <div
      className={cn(
        "flex items-center gap-3 rounded-xl border px-3 py-2.5",
        urgent
          ? "border-destructive/40 bg-destructive/10"
          : "border-warning/40 bg-warning/10",
        className,
      )}
    >
      <Clock
        className={cn("h-5 w-5 shrink-0", urgent ? "text-destructive" : "text-warning")}
      />
      <div className="min-w-0 flex-1">
        <p className="text-xs font-semibold leading-tight">
          Auto-deletes in {formatRemaining(remaining)}
        </p>
        <p className="text-[11px] leading-tight text-muted-foreground">
          Download now to keep it forever.
        </p>
      </div>
      {onDownload && (
        <Button
          size="sm"
          variant={urgent ? "destructive" : "default"}
          onClick={onDownload}
          disabled={downloadDisabled}
          className="h-8 shrink-0 px-3"
        >
          <Download className="mr-1 h-3.5 w-3.5" />
          Save
        </Button>
      )}
    </div>
  );
};
